import React from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

function About() {
  const features = [
    {
      icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
      title: 'AI-Powered Legal Letters',
      description: 'Our Grok AI engine analyzes your dispute and generates professional letters citing actual consumer protection laws.'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      title: 'Fast & Easy',
      description: 'Get your dispute letter in minutes, not hours. Simple form, instant results, ready to send.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: 'Secure & Private',
      description: 'Your data is encrypted and never shared. We take your privacy seriously with bank-level security.'
    },
    {
      icon: <SupportAgentIcon sx={{ fontSize: 40 }} />,
      title: 'Expert Support',
      description: 'Pro and Enterprise users get priority support to help you navigate complex disputes.'
    }
  ];

  const stats = [
    { value: '10,000+', label: 'Letters Generated' },
    { value: '85%', label: 'Success Rate' },
    { value: '$2.4M+', label: 'Recovered for Users' },
    { value: '24hr', label: 'Average Response Time' }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, md: 3 } }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
          <img 
            src="/logo.png" 
            alt="ComplainAI" 
            style={{ 
              height: '100px',
              width: 'auto'
            }} 
          />
        </Box>
        <Typography variant="h2" component="h1" gutterBottom fontWeight={700} sx={{ fontSize: { xs: '2rem', md: '3rem' } }}>
          About ComplainAI
        </Typography>
        <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', mt: 2 }}>
          AI-powered consumer advocacy that levels the playing field between you and big corporations.
        </Typography>
      </Box>

      {/* Mission Section */}
      <Paper elevation={3} sx={{ p: 5, mb: 6, bgcolor: 'primary.light' }}>
        <Typography variant="h4" gutterBottom fontWeight={600} align="center">
          Our Mission
        </Typography>
        <Typography variant="body1" paragraph align="center" sx={{ fontSize: '1.1rem', maxWidth: 900, mx: 'auto' }}>
          ComplainAI was created because we believe consumers deserve better. When companies overcharge, 
          deliver defective products, or refuse legitimate refunds, the legal system feels stacked against 
          the little guy. Hiring a lawyer is expensive. Writing an effective dispute letter takes hours of research.
        </Typography>
        <Typography variant="body1" align="center" sx={{ fontSize: '1.1rem', maxWidth: 900, mx: 'auto', fontWeight: 500 }}>
          We built ComplainAI to give you the power of AI and legal knowledge—instantly, affordably, 
          and without needing a law degree.
        </Typography>
      </Paper>

      {/* Stats Section */}
      <Grid container spacing={3} sx={{ mb: 8 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card elevation={2} sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h3" color="primary" fontWeight={700}>
                {stat.value}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                {stat.label}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* How It Works */}
      <Box sx={{ mb: 8 }}>
        <Typography variant="h4" gutterBottom fontWeight={600} align="center" sx={{ mb: 4 }}>
          How ComplainAI Works
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card elevation={2} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                      {feature.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" gutterBottom fontWeight={600}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Story Section */}
      <Paper elevation={3} sx={{ p: 5, mb: 6 }}>
        <Typography variant="h4" gutterBottom fontWeight={600} align="center">
          Why We Built This
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mt: 3 }}>
          After experiencing firsthand how difficult it is to get refunds from large companies—even when 
          you're clearly in the right—we knew there had to be a better way. Traditional consumer advocacy 
          is slow, expensive, and intimidating.
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
          With advances in AI, we realized we could automate the tedious work: researching relevant laws, 
          drafting professional letters, and citing legal precedents. What used to take hours now takes minutes.
        </Typography>
        <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
          ComplainAI democratizes consumer protection. Whether you're fighting a $50 airline baggage fee or 
          a $5,000 contractor dispute, you deserve the same level of professional advocacy as someone who 
          can afford a lawyer. That's what we deliver.
        </Typography>
      </Paper>

      {/* What We're Not */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 6, 
          p: 4, 
          bgcolor: 'background.paper', 
          border: '2px solid',
          borderColor: 'primary.main',
          borderRadius: 2 
        }}
      >
        <Typography variant="h5" gutterBottom fontWeight={600} color="primary.main">
          ⚠️ Important Disclaimers
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="body1" color="text.secondary" sx={{ minWidth: '30px' }}>✗</Typography>
            <Typography variant="body1">
              We are <strong>NOT</strong> a credit repair service
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="body1" color="text.secondary" sx={{ minWidth: '30px' }}>✗</Typography>
            <Typography variant="body1">
              We are <strong>NOT</strong> a law firm and do not provide legal advice
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="body1" color="text.secondary" sx={{ minWidth: '30px' }}>✗</Typography>
            <Typography variant="body1">
              We do <strong>NOT</strong> guarantee specific outcomes
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, fontStyle: 'italic' }}>
          ComplainAI generates professional dispute letters based on consumer protection laws. 
          The effectiveness depends on your specific situation and the company's policies.
        </Typography>
      </Paper>

      {/* CTA */}
      <Paper elevation={3} sx={{ p: 5, bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Ready to Stand Up for Your Rights?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem' }}>
          Join thousands of consumers who've successfully recovered money using ComplainAI.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Box
            component="a"
            href="/register"
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              textDecoration: 'none',
              display: 'inline-block',
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            Start Free Account
          </Box>
          <Box
            component="a"
            href="/contact"
            sx={{
              bgcolor: 'transparent',
              color: 'white',
              border: 2,
              borderColor: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              textDecoration: 'none',
              display: 'inline-block',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Contact Us
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default About;
